import { Component } from '@angular/core';
import { ApiService } from 'app/api.service';
import { AppService } from 'app/app.service';
import { UIModalBaseComponent } from 'app/ui/ui-modal-base.component';

@Component({
	selector: 'modal-activity-log',
	styleUrls: ['./modal-activity-log.component.scss'],
	templateUrl: './modal-activity-log.component.html'
})
export class ModalActivityLogComponent extends UIModalBaseComponent {

	id;
	type;
	activityLog;
	documents = [];

	// Set from the activity payload: the account and this project are both on the portal.
	portalEnabled = false;

	note = '';
	docName = '';
	docUrl = '';

	editNote = '';
	editing = null;

	// Document editing, kept separate from `editing`: that indexes activityLog, this indexes
	// documents, and a document has a name and a URL rather than one body of text.
	editingDoc = null;
	editDocName = '';
	editDocUrl = '';

	notesTab = { id: 'notes', description: 'Notes', count: 0 };
	documentsTab = { id: 'documents', description: 'Documents', count: 0 };

	selectedTab = 'notes';
	tabs = [this.notesTab, this.documentsTab];

	constructor(
		private api: ApiService,
		private app: AppService
	) {
		super();
	}

	open(data: any = null) {
		this.id = data['id'];
		this.type = data['type'];
		this.selectedTab = 'notes';
		this.notesTab.count = 0;
		this.documentsTab.count = 0;
		this.documents = [];
		this.portalEnabled = false;
		this.editingDoc = null;

		if (this.type === 'task') this.tabs = [this.notesTab];
		this.note = '';
		this.docName = '';
		this.docUrl = '';

		this.activityLog = null;
		this.reload(true);
	}

	reload(open = false) {
		this.api.activity.get(this.id, this.type, (activity) => {
			this.editing = null;
			this.portalEnabled = !!activity.portal_enabled;
			this.activityLog = [];
			this.documents = [];
			this.notesTab.count = 0;
			this.documentsTab.count = 0;

			activity.activities.forEach(item => {
				if (item.activity_type === 'document') {
					this.documentsTab.count += 1;
					this.documents.push(item);
				} else if (this.type !== 'task' || ['note', 'follow_up', 'resolve_follow_up'].includes(item.activity_type)) {
					this.notesTab.count += 1;
					this.activityLog.push(item);
				}

				if (item.json_data) {
					try {
						item.json_data = JSON.parse(item.json_data);
					} catch (ex) {
						item.json_data = null;
					}
				}
			});

			if (open) this.modal.open();

			if (this.type === 'quote') this.app.navigation.refreshQuoteNotes();
			if (this.type === 'project') this.app.navigation.refreshProjectNotes();
		}, error => {
			this.app.notifications.showDanger(error.message);
		});
	}

	newActivity() {
		if (this.note) {
			this.api.activity.create({ source: this.id, type: this.type, note: this.note }, () => {
				this.note = '';
				this.reload();
			}, error => {
				this.app.notifications.showDanger(error.message);
			});
		}
	}

	/**
	 * Whether this row can reach the customer portal at all.
	 *
	 * `portalEnabled` covers the account and the project — both have to be on the portal, and the
	 * server decides that. `from_project` covers the row: the portal reads activity.project_id, so a
	 * document attached to a quote has no portal to appear on, and the tick would do nothing there.
	 *
	 * Not gated on `my_note` like Edit and Delete: publishing is a project-management action, so
	 * anyone with access to the project can share a document a colleague added. activity/set_show_on_portal
	 * checks access to the project rather than who created the row.
	 */
	canShowOnPortal(activity): boolean {
		return this.portalEnabled && !!activity?.from_project;
	}

	toggleShowOnPortal(activity): void {
		const next = activity.show_on_portal ? 0 : 1;

		// Optimistic, so the tick responds immediately; rolled back if the save fails.
		activity.show_on_portal = next;

		this.api.activity.setShowOnPortal({
			id: activity.id,
			show_on_portal: next
		}, () => {}, error => {
			activity.show_on_portal = next ? 0 : 1;
			this.app.notifications.showDanger(error.message);
		});
	}

	editDocument(i): void {
		const d = this.documents[i];
		this.editDocName = d.note;
		this.editDocUrl = d.json_data?.url || '';
		this.editingDoc = i;
	}

	cancelEditDocument(): void {
		this.editingDoc = null;
	}

	saveDocument(): void {
		if (this.editingDoc === null) return;

		if (!this.editDocName) {
			this.app.notifications.showDanger('Please enter document name.');
			return;
		}

		if (!this.editDocUrl) {
			this.app.notifications.showDanger('Please enter document URL.');
			return;
		}

		let url = this.editDocUrl;
		if (!url.includes('://')) url = 'https://' + url;

		const d = this.documents[this.editingDoc];

		this.api.activity.update({
			id: d.id,
			note: this.editDocName,
			// json_data goes in full: update() rewrites the whole blob, so omitting it would drop the
			// URL. show_on_portal is deliberately left out — the API only writes that when it's sent,
			// so renaming a document doesn't unshare it.
			json_data: { ...(d.json_data || {}), url }
		}, () => {
			this.editingDoc = null;
			this.reload();
		}, error => {
			this.app.notifications.showDanger(error.message);
		});
	}

	newDocument() {
		if (!this.docName) {
			this.app.notifications.showDanger('Please enter document name.');
			return;
		}

		if (!this.docUrl) {
			this.app.notifications.showDanger('Please enter document URL.');
			return;
		}

		if (!this.docUrl.includes('://')) {
			this.docUrl = 'https://' + this.docUrl;
		}

		this.api.activity.create({ source: this.id, type: this.type, note: this.docName, activity_type: 'document', json_data: { url: this.docUrl, display_order: 9999 } }, () => {
			this.docName = '';
			this.docUrl = '';
			this.reload();
		}, error => {
			this.app.notifications.showDanger(error.message);
		});
	}

	// Customer-side activity either comes from the customer portal — attributed to a
	// portal_user, so we know the person — or from an emailed public link, which
	// stays anonymous. Empty when there is no portal user behind the row.
	viewerName(activity) {
		return activity?.portal_user_name || activity?.portal_user_email || '';
	}

	// The same, but falling back to the generic "Customer" for public-link activity.
	viewerLabel(activity) {
		return this.viewerName(activity) || 'Customer';
	}

	editMode(i) {
		this.editNote = this.activityLog[i].note;
		this.editing = i;
	}

	save() {
		if (!this.editing === null) return;

		const a = this.activityLog[this.editing];
		this.api.activity.update({
			id: a.id,
			note: this.editNote
		}, () => {
			this.reload();
			this.editing = null;
			this.note = '';
		}, error => {
			this.app.notifications.showDanger(error.message);
		});
	}

	confirmDelete(i) {
		this.app.dialog.pop('Delete Note', 'Are you sure you want to delete "' + this.activityLog[i].note + '"?', ['Cancel', '*Delete'], (button) => {
			if (button === 1) {
				this.api.activity.delete({ id: this.activityLog[i].id }, () => {
					this.reload();
				}, error => {
					this.app.notifications.showDanger(error.message);
				});
			}
		});
	}

	deleteDocument(item) {
		this.app.dialog.pop('Delete Document', 'Are you sure you want to delete "' + item.note + '"?', ['Cancel', '*Delete'], (button) => {
			if (button === 1) {
				this.api.activity.delete({ id: item.id }, () => {
					this.reload();
				}, error => {
					this.app.notifications.showDanger(error.message);
				});
			}
		});
	}

}
